import {Injectable} from '@angular/core';
import {catchError, map} from 'rxjs/operators';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, of, throwError} from 'rxjs';
import {subscriptionStatusUrl} from "./tax-planning.service";
import {TaxplanIQServices} from "../services/TaxplanIQServices";
import {SOURCE_TYPE} from "../constants/Tpiq.constants";
import {UrlService} from "../services/UrlService";

export function getPaginatedTaxStrategies(org, userId, includeCustomRates, applyLock, _strategyStatus) {
    let path = `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/list`;

    const params: string[] = [];

    if (includeCustomRates) {
      params.push('includeCustomRates=true');
    }

    if (applyLock) {
      params.push('applyLock=true');
    }

    if (params.length) {
      path += `?${params.join('&')}`;
    }

    return path;
}

export function saveNewTaxStrategy(org, userId) {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/add`;
}

export function updateTaxStrategy(org, userId, id) {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/${id}`;
}

export function getTaxStrategy(org, userId, id) {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/${id}`;
}

export function getDeleteTaxStrategyUrl(org, userId, id, acceptence) {
  let url = `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/${id}`;
  if (acceptence == 'yes') {
    url += '?deleteChildren=true';
  } else if (acceptence == 'no') {
    url += '?deleteChildren=false';
  }

  return url;
}

export function deleteTaxStrategy(org, userId, id) {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/${id}`;
}

export function getDocumentUrl(orgId, userId) {
    return `/DocumentServices/orgs/${orgId}/users/${userId}/document`;
}

export function getVideoDocumentUrl(orgId, userId) {
    return `/orgs/${orgId}/users/${userId}/document`;
}

export function getFirmLogoUploadUrl(orgId, userId) {
  return `/orgs/${orgId}/users/${userId}/document/logos`;
}

export function getDocumentDownloadUrl(userId, orgId, id, type?) {
  let url = `/TaxPlanningServices/orgs/${orgId}/users/${userId}/document/${id}`;
  if (type) {
    url += `?type=${type}`;
  }
  return url;
}

export function getAbilityOfPublishStrategyUrl(org, userId, strategyId) {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/${strategyId}/publish`;
}

export function acceptUpdatesStrategyUrl(org, userId, fromStrategyId, toStrategyId, type) {
  let path = `/TaxPlanningServices/orgs/${org}/users/${userId}/import/${fromStrategyId}/to/${toStrategyId}`;
  if (type === 'accept') {
    path += '?action=accept';
  } else if (type === 'deny'){
    path += '?action=deny';
  }
  return path;
}

export function getSuggestStrategyChangesUrl(org, userId, strategyId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/${strategyId}/suggest`;
}

export function getSaveStrategyRuleUrl(org, userId, strategyId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/${strategyId}/save-StrategyRule`;
}

export function getTaxPlanningFirmsUrl(org, userId) {
  const path = `/TaxPlanningServices/orgs/${org}/users/${userId}/taxplanning-firms`;
  return path;
}

export function getUpdateTaxPlanningFirmUrl(org, userId, firmId) {
  const path = `/TaxPlanningServices/orgs/${org}/users/${userId}/taxplanning-firms/edit/${firmId}`;
  return path;
}

export function getAcceptOrDenyDeletedByParentUrl(org, userId, strategyId, actionType) {
  const path = `/TaxPlanningServices/orgs/${org}/users/${userId}/import/delete?strategyId=${strategyId}&action=${actionType}`;
  return path;
}

export function acceptUpdatesOfAllStrategiesUrl(org, userId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/import/all`;
}

export function getTaxStrategyDashboardDataUrl(org, userId, strategyId) {
    return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/${strategyId}/dashboard`;
}

export function getUpdateStrategyCriteriaStatusUrl(org, userId, taskId) {
  return `/TaxPlanningServices/org/${org}/users/${userId}/task/${taskId}/checklist`;
}

export function getAddNewTagUrl(org, userId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/tags`;
}

export function getFirmLabelsUrl(org, userId) {
  return `/TaxPlanningServices/org/${org}/users/${userId}/clients/labels`;
}

export function getEstimateSuggestionsUrl(org, userId) {
  return `/TaxPlanningServices/org/${org}/users/${userId}/taxestimates/suggestions`;
}

export function getArchiveStrategyUrl(org, userId, strategyId) {
  return `/TaxPlanningServices/orgs/${org}/users/${userId}/strategy/${strategyId}`;
}


@Injectable({
    providedIn: 'root'
})
export class ManageTaxStrategiesServices extends  TaxplanIQServices {
    firmTagsData: any;
    firmLabelsData: any;
    options = {};
    constructor(http: HttpClient) {
        super(http);
    }

    getPaginatedTaxStrategies(org, userId, obj, includeCustomRates?, applyLock?, strategyStatus?): Observable<any> {
        const url = getPaginatedTaxStrategies(org, userId, includeCustomRates, applyLock, strategyStatus);
        const payload = {
          sortKey: 'name',
          sortOrder: 'asc',
          fromIndex: 0,
          searchFilters: [],
          status: 'Active',
          category: '',
          complexity: '',
          urgency: '',
          riskLevel: '',
          burden: '',
          amountType: '',
          applicableTo: '',
          requiresInvestment: '',
          searchKey: '',
          ...(obj || {})
        };

        // Override status when explicitly provided
        if (strategyStatus) {
          payload['status'] = strategyStatus;
        }

        return this.create(url, payload, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    addNewTaxStrategy(org, userId, obj): Observable<any> {
        const url = saveNewTaxStrategy(org, userId);
        return this.create(url, obj, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    updateTaxStrategy(org, userId, obj): Observable<any> {
        const url = updateTaxStrategy(org, userId, obj.id);
        return this.update(url, obj, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTaxStrategyId(org, userId, id): Observable<any> {
        const url = getTaxStrategy(org, userId, id);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    removeTaxStrategy(org, userId, id, acceptence): Observable<any> {
        const url = getDeleteTaxStrategyUrl(org, userId, id, acceptence);
        return this.delete(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getDocumentServiceUrl(orgId, userId): string {
        let url = getDocumentUrl(orgId, userId);
        url = UrlService.getBaseUrl('JAVA') + url;
        return url;
    }

    getVideoDocumentServiceUrl(orgId, userId): string {
        let url = getVideoDocumentUrl(orgId, userId);
        url = UrlService.getBaseUrl('DOCUMENT') + url;
        return url;
    }

    getFirmLogoUrl(orgId, userId): string {
      let url = getFirmLogoUploadUrl(orgId, userId);
      url = UrlService.getBaseUrl('DOCUMENT') + url;
      return url;
    }

    downloadDocument(userId, orgId, id, type?): Observable<any> {
        const url = getDocumentDownloadUrl(userId, orgId, id, type);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    removeDocument(userId, orgId, id): Observable<any> {
        const url = getDocumentDownloadUrl(userId, orgId, id);
        return this.delete(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    abilityOfPublishStrategy(org, userId, strategyId): Observable<any> {
        const url = getAbilityOfPublishStrategyUrl(org, userId, strategyId);
        return this.update(url, {}, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    acceptUpdates(org, userId, fromStrategyId, toStrategyId, type): Observable<any> {
        const url = acceptUpdatesStrategyUrl(org, userId, fromStrategyId, toStrategyId, type);
        return this.create(url, {}, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    suggestStrategyChanges(org, userId, strategyId): Observable<any> {
        const url = getSuggestStrategyChangesUrl(org, userId, strategyId);
        return this.update(url, {}, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    saveStrategyRule(org, userId, strategyId, obj): Observable<any> {
      const url = getSaveStrategyRuleUrl(org, userId, strategyId);
      return this.create(url, obj, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTaxPlanningFirms(org, userId): Observable<any> {
        const url = getTaxPlanningFirmsUrl(org, userId);
        return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    updateTaxPlanningFirm(org, userId, firm): Observable<any> {
        const url = getUpdateTaxPlanningFirmUrl(org, userId, firm.id);
        return this.update(url, firm, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    acceptOrDenyDeletedByParent(org, userId, strategyId, type): Observable<any> {
      const url = getAcceptOrDenyDeletedByParentUrl(org, userId, strategyId, type);
      return this.delete(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    acceptUpdatesOfAllStrategies(org, userId): Observable<any> {
      const url = acceptUpdatesOfAllStrategiesUrl(org, userId);
      return this.create(url, {}, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTaxStrategyDashboardData(org, userId, id): Observable<any> {
      const url = getTaxStrategyDashboardDataUrl(org, userId, id);
      return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    updateStrategyCriteriaStatus(org, userId, taskId, data): Observable<any> {
      const url = getUpdateStrategyCriteriaStatusUrl(org, userId, taskId);
      return this.update(url, data, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    addNewTagNameToFirmsList(org, userId, obj): Observable<any> {
      const url = getAddNewTagUrl(org, userId);
      return this.create(url, obj, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    getTagsDataForFirm(org, userId): Observable<any> {
      if (this.firmTagsData){
        return of(this.firmTagsData);
      }
      const url = getAddNewTagUrl(org, userId);
      return this.query(url, SOURCE_TYPE.JAVA).pipe(map(res => {
        this.firmTagsData = res;
        return this.firmTagsData;
      }), catchError(e => this.handleError(e)));
    }

    getLabelsDataForFirm(org, userId): Observable<any> {
      if (this.firmLabelsData){
        return of(this.firmLabelsData);
      }
      const url = getFirmLabelsUrl(org, userId);
      return this.query(url, SOURCE_TYPE.JAVA).pipe(map(res => {
        this.firmLabelsData = res;
        return this.firmLabelsData;
      }), catchError(e => this.handleError(e)));
    }

    getEstimateStrategiesSuggestions(org, userId): Observable<any> {
      const url = getEstimateSuggestionsUrl(org, userId);
      return this.query(url, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    archiveStrategy(orgId, userId, strategyId, obj){
      const url = getArchiveStrategyUrl(orgId, userId, strategyId);
      return this.patch(url, obj, SOURCE_TYPE.JAVA).pipe(catchError(this.handleError));
    }

    clearFirmTagsData() {
      this.firmTagsData = '';
    }

    clearFirmLabelsData() {
      this.firmLabelsData = '';
    }
  private handleError(error: HttpErrorResponse): any {
    return throwError(error.error);
  }

}
